import { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form, useNavigate } from 'react-router-dom';

import Input from '../../../components/shared/Input';
import Button from '../../../components/shared/Button';

import { ADMINISTRATION_PATH } from '../../../constants';
import {
  extractMultipleErrors,
  hasNestedObjects,
  showToast,
} from '../../../utils';

import { useAuth } from '../../../context/auth-context';
import { useValidationRules } from '../../../hooks/useValidation';

import ForumMembersStore from '../services/forumMembers.store';
const forumMembersStore: ForumMembersStore = new ForumMembersStore();

export interface ResetPasswordFormData {
  oldPassword: string;
  password: string;
  repeated_password: string;
}

const UserResetPasswordForm: React.FC = () => {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { PASSWORD_VALIDATION_RULES } = useValidationRules();

  const [beError, setBeError] = useState(false);

  const methods = useForm<ResetPasswordFormData>({
    defaultValues: {
      oldPassword: '',
      password: '',
      repeated_password: '',
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    watch,
    setError,
  } = methods;

  const onSubmit: SubmitHandler<ResetPasswordFormData> = async (data) => {
    setBeError(false);

    if (isValid) {
      const response = await forumMembersStore.setNewPassword(data);
      const { success, message } = response;

      const handleError = (message: any) => {
        if (hasNestedObjects(message)) {
          // Set input fields errors
          const errorMessages = extractMultipleErrors(message);

          for (const key in errorMessages) {
            if (key in methods.getValues()) {
              setError(key as keyof ResetPasswordFormData, {
                type: 'backend',
                message: errorMessages[key],
              });
            }
          }
        } else {
          // Show toast error
          showToast(message, success);
          setBeError(true);
        }
      };

      if (success) {
        showToast(message, true);
        navigate(`${ADMINISTRATION_PATH}/${userId}`);
      } else {
        handleError(message);
      }
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <Form
          method="post"
          className="pks-layout-col-xl w-full"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="pks-layout-col">
            <Input
              type="password"
              id="oldPassword"
              name="oldPassword"
              label={`${t('inputs.old_password.label')} *`}
              placeholder={t('inputs.old_password.placeholder')}
              autoFocus
              autoComplete="off"
              validationRules={PASSWORD_VALIDATION_RULES}
              beError={beError}
            />
            <Input
              type="password"
              id="password"
              name="password"
              label={`${t('inputs.new_password.label')} *`}
              placeholder={t('inputs.new_password.placeholder')}
              autoComplete="off"
              validationRules={PASSWORD_VALIDATION_RULES}
              beError={beError}
            />
            <Input
              type="password"
              id="repeated_password"
              name="repeated_password"
              label={`${t('inputs.confirm_password.label')} *`}
              placeholder={t('inputs.confirm_password.placeholder')}
              autoComplete="off"
              validationRules={{
                ...PASSWORD_VALIDATION_RULES,
                validate: (value: string): any => {
                  if (watch('password') !== value) {
                    return t('validation.password_equality');
                  }
                },
              }}
              beError={beError}
            />
          </div>
          {/* Submit Button */}
          <Button type="submit" wide disabled={isSubmitting}>
            {isSubmitting ? t('state.submitting') : t('state.submit')}
          </Button>
        </Form>
      </FormProvider>
    </>
  );
};

export default UserResetPasswordForm;
